import { useMediaQuery } from '@ag1/ui';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useProductStore } from '@portal/state/store/pdpStore';
import { useMemo } from 'react';
import { ImageCarousel } from 'shared/ui/ImageCarousel';
import {
  type BaseImage,
  ImageCarouselPDP as ImageCarouselPDPprops,
  SanityKeyed
} from 'types/studio/studio';
import { CommonSectionProps } from '../../types';
import { formatPdpCarouselImages, getSelectedVariant, getUniqueVariantId } from '../utils';

type ImageCarouselAssets = SanityKeyed<BaseImage>[];

const ImageCarouselPDP = (props: SanityKeyed<ImageCarouselPDPprops> & CommonSectionProps) => {
  const store = useStoreValue();

  const { disableThumbnailsMobile, disableThumbnailsDesktop } = props;
  const variants = useProductStore((state) => state.variants);
  const pdpType = useProductStore((state) => state.pdpType);
  const bundleVariant = useProductStore((state) => state.bundleVariant);
  const isOtpProduct = useProductStore((state) => state.isOtpProduct);
  const aboveSmall = useMediaQuery('s');
  const aboveMedium = useMediaQuery('m');
  const flavor = useProductStore((state) => state.flavor);
  const defaultVariantId = useMemo(
    () => (variants && variants[0] ? getUniqueVariantId(variants[0], flavor) : ''),
    [variants, flavor]
  );

  const selectedVariantId = useProductStore((state) => state.selectedVariantId) || defaultVariantId;

  const isOtpPdP = pdpType === 'otpView';
  const isBundlePdP = pdpType === 'bundleView';

  const disableThumbnails =
    (!aboveMedium && disableThumbnailsMobile) || (aboveMedium && disableThumbnailsDesktop);

  const activeVariant = useMemo(
    () =>
      isBundlePdP
        ? bundleVariant
        : getSelectedVariant(variants, selectedVariantId ?? '', store, flavor),
    [isBundlePdP, bundleVariant, variants, selectedVariantId, flavor, store]
  );

  const formattedImages = useMemo(() => {
    let images: ImageCarouselAssets | undefined;

    const showOTPImages = isOtpPdP || (!isBundlePdP && isOtpProduct);
    const showSpecificFlavorImages = flavor !== 'original' && activeVariant?.flavorsImages;

    if (showOTPImages && !showSpecificFlavorImages) {
      if (!aboveSmall && activeVariant?.withMobileImage) {
        images = formatPdpCarouselImages(activeVariant?.carouselImagesOTPMobile);
      } else {
        images = formatPdpCarouselImages(activeVariant?.carouselImagesOTP);
      }
    } else if (showSpecificFlavorImages) {
      const selectedFlavor = activeVariant?.flavorsImages?.find(
        ({ flavorQuery }) => flavorQuery === flavor
      );

      if (selectedFlavor) {
        if (showOTPImages) {
          if (!aboveSmall && selectedFlavor?.flavorWithMobileImage) {
            images = formatPdpCarouselImages(selectedFlavor?.carouselImagesFlavorMobileOTP);
          } else {
            images = formatPdpCarouselImages(selectedFlavor?.carouselImagesFlavorOTP);
          }
        } else if (!aboveSmall && selectedFlavor.flavorWithMobileImage) {
          images = formatPdpCarouselImages(selectedFlavor?.carouselImagesFlavorMobile);
        } else {
          images = formatPdpCarouselImages(selectedFlavor?.carouselImagesFlavor);
        }
      }
    } else if (!aboveSmall && activeVariant?.withMobileImage) {
      images = formatPdpCarouselImages(activeVariant?.carouselImagesMobile);
    } else {
      images = formatPdpCarouselImages(activeVariant?.carouselImages);
    }

    return images;
  }, [isOtpPdP, isBundlePdP, isOtpProduct, flavor, activeVariant, aboveSmall]);

  if (!formattedImages?.length) return null;

  return (
    <ImageCarousel
      isPdp
      enableThumbnails={!disableThumbnails}
      images={formattedImages}
      thumbnails={formattedImages}
      sizes={{ xxs: '100vw', m: '50vw', l: '800px' }}
    />
  );
};

export default ImageCarouselPDP;
